import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Asset : famille d'entités "bien suivi".
 *
 * PREMIÈRE famille du socle construite sur le modèle "famille + type" de la
 * bible (cf. WIP-FAMILLES-ENTITES). Elle sert de PATRON pour les suivantes.
 *
 * Principe : le socle ne connaît PAS d'entité concrète ("chat", "vélo",
 * "voiture"). Il connaît la famille générique `asset` ; le vocabulaire métier
 * est porté par le champ `type` (chaîne libre, déclarée par l'app : "cat",
 * "bike"...), et le nom affiché à l'écran par `label` côté app. Le bar à chats
 * crée des `asset` de type "cat" ; l'atelier des `asset` de type "bike".
 *
 * Un asset peut appartenir à l'organisation (le chat résident) ou à un tiers
 * (le chien confié par un client) : dans ce cas `ownerContactId` pointe la
 * fiche `contact` du propriétaire. Ce n'est PAS le mécanisme owner-scoped
 * Firebase (qui borne par uid) : asset reste org-wide, `ownerContactId` est
 * une donnée métier, pas une frontière de sécurité.
 *
 * Convention de patron (réutilisée par toutes les familles) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création (champs
 *     métier ; les champs standard sont remplis par le SDK). Sert à VALIDER
 *     l'entrée AVANT toute écriture Firestore.
 */

/** Champs métier de la famille asset (hors champs standard du socle). */
export const assetCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "cat", "bike"). */
  type: z.string().min(1),
  /** Nom d'affichage de l'exemplaire (ex. "Felix", "VTT n°3"). */
  name: z.string().min(1),
  /** Libellé de la famille tel que l'app veut l'afficher (ex. "Chat"). Optionnel. */
  label: z.string().min(1).optional(),
  /** État courant, chaîne libre pilotée par l'app (ex. "present", "vet", "adopted"). */
  status: z.string().min(1).optional(),
  /** Référence externe / n° de série / n° de puce, optionnel. */
  reference: z.string().min(1).optional(),
  /** Fiche `contact` du propriétaire si le bien appartient à un tiers, optionnel. */
  ownerContactId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille asset (champs standard + champs métier). */
export const AssetSchema = z.object({
  ...baseEntityFields,
  ...assetCoreFields,
});

/** Type d'un asset. */
export type Asset = z.infer<typeof AssetSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const AssetCreateInputSchema = z.object({
  ...assetCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un asset. */
export type AssetCreateInput = z.infer<typeof AssetCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const AssetUpdateInputSchema = z
  .object({ ...assetCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un asset. */
export type AssetUpdateInput = z.infer<typeof AssetUpdateInputSchema>;
